import { useState, useId } from 'react';
import { motion } from 'motion/react';
import {
  WhatsappLogo, Phone, CheckCircle, PaperPlaneTilt, Warning,
} from '@phosphor-icons/react';
import { buildWAUrl, TEL } from '../lib/whatsapp';
import { trackFormSubmit } from '../lib/analytics';
import { getUtmParams, formatUtmForMessage } from '../lib/utm';

// --- Types -------------------------------------------------------------------

export type LeadFormVariant = 'consultation' | 'visa' | 'package' | 'contact';

interface VariantConfig {
  btnLabel: string;
  headline: string;
  subline: string;
  defaultService?: string;
  destPlaceholder?: string;
}

const VARIANT_CONFIG: Record<LeadFormVariant, VariantConfig> = {
  consultation: {
    btnLabel: 'Get Free Consultation',
    headline: 'Get Your Free Consultation',
    subline: "Tell us about your travel plans and we'll craft the perfect itinerary.",
    destPlaceholder: 'e.g. Maldives, Europe, Turkey...',
  },
  visa: {
    btnLabel: 'Start Visa Application',
    headline: 'Start Your Visa Application',
    subline: 'Our visa experts will guide you through every step with fast, accurate requirements-based support.',
    defaultService: 'UAE Visit Visa',
    destPlaceholder: 'e.g. India, Pakistan, Philippines...',
  },
  package: {
    btnLabel: 'Get Package Quote',
    headline: 'Get a Price Quote',
    subline: "Fill in your details and we'll send a tailored quote within 1 hour.",
    defaultService: 'Holiday Package',
    destPlaceholder: 'e.g. Bali, Dubai, Istanbul...',
  },
  contact: {
    btnLabel: 'Send Enquiry',
    headline: 'Send Us an Enquiry',
    subline: "We'll get back to you within 1 hour.",
    destPlaceholder: 'e.g. Maldives, Schengen, Dubai...',
  },
};

const SERVICE_OPTIONS = [
  'Holiday Package', 'Umrah Package', 'UAE Visit Visa', 'Schengen Visa',
  'UK Visa', 'USA Visa', 'Canada Visa', 'Australia Visa',
  'Flight Tickets', 'Hotel Booking', 'UAE Tours & Activities',
  'Travel Insurance', 'Corporate Travel', 'Other',
];

const VISA_SERVICE_OPTIONS = [
  'UAE Visit Visa',
  'UAE Visa Extension',
  'UAE Inside Country Visa Change',
  'UAE Tourist Visa',
  'Other Visa Service',
];

// --- Form state ---------------------------------------------------------------

interface FormState {
  name: string;
  whatsapp: string;
  email: string;
  service: string;
  destination: string;
  date: string;
  travellers: string;
  message: string;
}

interface FormErrors {
  name?: string;
  whatsapp?: string;
  service?: string;
  destination?: string;
  date?: string;
}

// --- Props --------------------------------------------------------------------

export interface LeadFormProps {
  variant?: LeadFormVariant;
  packageName?: string;
  defaultService?: string;
  className?: string;
  layout?: 'default' | 'compactEnquiry';
  /** Hide the headline + subline (useful when the parent provides its own heading) */
  hideHeading?: boolean;
}

// --- Component ----------------------------------------------------------------

export default function LeadForm({
  variant = 'consultation',
  packageName,
  defaultService,
  className = '',
  layout = 'default',
  hideHeading = false,
}: LeadFormProps) {
  const uid = useId();
  const fid = (field: string) => `lead-${uid}-${field}`;
  const config = VARIANT_CONFIG[variant];
  const initialService = defaultService ?? config.defaultService ?? '';
  const serviceOptions = variant === 'visa'
    ? Array.from(new Set([initialService, ...VISA_SERVICE_OPTIONS].filter(Boolean)))
    : SERVICE_OPTIONS;

  const [form, setForm] = useState<FormState>({
    name: '', whatsapp: '', email: '',
    service: initialService,
    destination: '', date: '',
    travellers: '2', message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [waUrl, setWaUrl] = useState('');

  // -- Validation --------------------------------------------------------------
  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!form.name.trim())     e.name     = 'Name is required.';
    if (!form.whatsapp.trim()) e.whatsapp = 'WhatsApp number is required.';
    if (!form.service)         e.service  = 'Please select a service.';
    if (variant === 'visa' && !form.destination.trim()) e.destination = 'Nationality is required.';
    if (variant === 'visa' && !form.date.trim())        e.date        = 'Travel date is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const clearError = (field: keyof FormErrors) =>
    setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (name in errors) clearError(name as keyof FormErrors);
  };

  // -- Submit ------------------------------------------------------------------
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const source    = typeof window !== 'undefined' ? window.location.href : '';
    const device    = typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop';
    const pageTitle = typeof document !== 'undefined' ? document.title : '';
    const pagePath  = typeof window !== 'undefined' ? window.location.pathname : '';
    const timestamp = new Date().toLocaleString('en-AE', { timeZone: 'Asia/Dubai' });
    const utm       = getUtmParams();
    const utmStr    = formatUtmForMessage(utm);

    // Fire GA4 + Meta Pixel conversion events
    trackFormSubmit({
      name:         form.name,
      phone:        form.whatsapp,
      service:      form.service,
      message:      form.message,
      formType:     variant,
      sourcePage:   pagePath,
      sourceTitle:  pageTitle,
      timestamp,
      ...utm,
    });

    const lines = [
      `Hi Big Bird Travel - new enquiry from website:`,
      ``,
      `Name:       ${form.name}`,
      `WhatsApp:   ${form.whatsapp}`,
      ...(form.email       ? [`Email:      ${form.email}`]           : []),
      `Service:    ${form.service}`,
      ...(packageName      ? [`Package:    ${packageName}`]          : []),
      ...(form.destination ? [`Destination: ${form.destination}`]    : []),
      ...(form.date        ? [`Travel Date: ${form.date}`]           : []),
      `Travellers: ${form.travellers}`,
      ...(form.message     ? [``, `Message:`, form.message]          : []),
      ``,
      `-- Lead Source --`,
      `Page:   ${pageTitle}`,
      `URL:    ${source}`,
      `Device: ${device}`,
      `Time:   ${timestamp} (UAE)`,
      ...(utmStr ? [``, `-- Campaign --`, utmStr] : []),
    ];

    const url = buildWAUrl(lines.join('\n'));
    setWaUrl(url);
    setSubmitted(true);
  };

  // -- Success state -----------------------------------------------------------
  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35 }}
        className={`bg-[#E1F5EE] rounded-2xl p-8 text-center ${className}`}
      >
        <CheckCircle size={52} weight="fill" className="text-[#28C840] mx-auto mb-4" />
        <h3 className="font-display font-black text-[#071126] text-xl mb-2">
          Thank you, {form.name.split(' ')[0]}!
        </h3>
        <p className="text-gray-600 text-[14px] mb-1 leading-relaxed">
          Our UAE-based travel consultant will contact you shortly.
        </p>
        <p className="text-gray-400 text-[13px] mb-7">
          For faster assistance, continue the conversation on WhatsApp.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#0F7A35] text-white px-7 py-3.5 rounded-xl font-black text-[12px] uppercase tracking-wider hover:bg-[#20ba5a] transition-colors shadow-lg"
          >
            <WhatsappLogo size={17} weight="fill" /> WhatsApp Now
          </a>
          <a
            href={TEL}
            className="inline-flex items-center justify-center gap-2 bg-[#071126] text-white px-7 py-3.5 rounded-xl font-black text-[12px] uppercase tracking-wider hover:bg-[#0077CC] transition-colors"
          >
            <Phone size={17} weight="fill" /> Call Now
          </a>
        </div>
      </motion.div>
    );
  }

  // -- Shared input styling -----------------------------------------------------
  const base = 'w-full bg-[#F5F5F5] rounded-xl px-4 py-3 text-[#071126] font-medium text-[13px] outline-none focus:ring-2 focus:ring-[#0096FF] placeholder:text-gray-300 transition-shadow';
  const err  = 'border-2 border-red-300 bg-red-50/40 focus:ring-red-300';

  if (layout === 'compactEnquiry') {
    return (
      <div className={`lead-compact-enquiry ${className}`}>
        {!hideHeading && (
          <div className="lead-compact-heading">
            <h3>{config.headline}</h3>
            <p>{config.subline}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="lead-compact-form">
          <div className="lead-compact-field">
            <label htmlFor={fid('name')}>
              Full Name <span>*</span>
            </label>
            <input
              id={fid('name')}
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your full name"
              className={errors.name ? 'lead-compact-error' : ''}
            />
            {errors.name && <FieldError msg={errors.name} />}
          </div>

          <div className="lead-compact-field">
            <label htmlFor={fid('whatsapp')}>
              WhatsApp Number <span>*</span>
            </label>
            <input
              id={fid('whatsapp')}
              name="whatsapp"
              value={form.whatsapp}
              onChange={handleChange}
              placeholder="+971 5X XXX XXXX"
              type="tel"
              className={errors.whatsapp ? 'lead-compact-error' : ''}
            />
            {errors.whatsapp && <FieldError msg={errors.whatsapp} />}
          </div>

          <div className="lead-compact-field">
            <label htmlFor={fid('email')}>
              Email <small>(optional)</small>
            </label>
            <input
              id={fid('email')}
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@example.com"
              type="email"
            />
          </div>

          <div className="lead-compact-field">
            <label htmlFor={fid('service')}>
              Service Required <span>*</span>
            </label>
            <select
              id={fid('service')}
              name="service"
              value={form.service}
              onChange={handleChange}
              className={errors.service ? 'lead-compact-error' : ''}
            >
              <option value="">Select a service...</option>
              {serviceOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.service && <FieldError msg={errors.service} />}
          </div>

          <div className="lead-compact-field">
            <label htmlFor={fid('destination')}>
              {variant === 'visa' ? 'Visa Country / Nationality' : 'Destination'}
              {variant === 'visa' && <span> *</span>}
            </label>
            <input
              id={fid('destination')}
              name="destination"
              value={form.destination}
              onChange={handleChange}
              placeholder={config.destPlaceholder ?? 'e.g. Dubai, Maldives...'}
              className={errors.destination ? 'lead-compact-error' : ''}
            />
            {errors.destination && <FieldError msg={errors.destination} />}
          </div>

          <div className="lead-compact-field">
            <label htmlFor={fid('date')}>
              Travel Date
              {variant === 'visa' && <span> *</span>}
            </label>
            <input
              id={fid('date')}
              name="date"
              value={form.date}
              onChange={handleChange}
              type="date"
              min={new Date().toISOString().split('T')[0]}
              className={errors.date ? 'lead-compact-error' : ''}
            />
            {errors.date && <FieldError msg={errors.date} />}
          </div>

          <div className="lead-compact-field">
            <label htmlFor={fid('travellers')}>
              Travellers <small>(optional)</small>
            </label>
            <select id={fid('travellers')} name="travellers" value={form.travellers} onChange={handleChange}>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '10+'].map(n => (
                <option key={n} value={n}>
                  {n} {n === '1' ? 'person' : 'people'}
                </option>
              ))}
            </select>
          </div>

          <div className="lead-compact-field lead-compact-message">
            <label htmlFor={fid('message')}>Additional Details</label>
            <textarea
              id={fid('message')}
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={3}
              placeholder="Budget, preferences, special requests..."
            />
          </div>

          <button type="submit" className="lead-compact-submit">
            <PaperPlaneTilt size={18} weight="fill" />
            {config.btnLabel}
          </button>

          <p className="lead-compact-note">
            No spam. No account needed. We reply within 1 hour.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className={className}>
      {!hideHeading && (
        <div className="mb-6">
          <h3 className="font-display font-black text-[#071126] text-xl mb-1">{config.headline}</h3>
          <p className="text-gray-500 text-[13px] leading-relaxed">{config.subline}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">

        {/* -- Row 1: Name + WhatsApp -- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={fid('name')} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1.5 block">
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              id={fid('name')}
              name="name" value={form.name} onChange={handleChange}
              placeholder="Your full name"
              className={`${base} ${errors.name ? err : ''}`}
            />
            {errors.name && <FieldError msg={errors.name} />}
          </div>
          <div>
            <label htmlFor={fid('whatsapp')} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1.5 block">
              WhatsApp Number <span className="text-red-400">*</span>
            </label>
            <input
              id={fid('whatsapp')}
              name="whatsapp" value={form.whatsapp} onChange={handleChange}
              placeholder="+971 5X XXX XXXX" type="tel"
              className={`${base} ${errors.whatsapp ? err : ''}`}
            />
            {errors.whatsapp && <FieldError msg={errors.whatsapp} />}
          </div>
        </div>

        {/* -- Row 2: Email + Service -- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={fid('email')} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1.5 block">
              Email <span className="text-gray-300 font-medium normal-case tracking-normal">(optional)</span>
            </label>
            <input
              id={fid('email')}
              name="email" value={form.email} onChange={handleChange}
              placeholder="you@example.com" type="email"
              className={base}
            />
          </div>
          <div>
            <label htmlFor={fid('service')} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1.5 block">
              Service Required <span className="text-red-400">*</span>
            </label>
            <select
              id={fid('service')}
              name="service" value={form.service} onChange={handleChange}
              className={`${base} cursor-pointer ${errors.service ? err : ''}`}
            >
              <option value="">Select a service...</option>
              {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.service && <FieldError msg={errors.service} />}
          </div>
        </div>

        {/* -- Row 3: Destination + Travel Date -- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={fid('destination')} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1.5 block">
              {variant === 'visa' ? 'Visa Country' : 'Destination'}
            </label>
            <input
              id={fid('destination')}
              name="destination" value={form.destination} onChange={handleChange}
              placeholder={config.destPlaceholder ?? 'e.g. Dubai, Maldives...'}
              className={base}
            />
          </div>
          <div>
            <label htmlFor={fid('date')} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1.5 block">
              Travel Date
            </label>
            <input
              id={fid('date')}
              name="date" value={form.date} onChange={handleChange}
              type="date"
              min={new Date().toISOString().split('T')[0]}
              className={base}
            />
          </div>
        </div>

        {/* -- Row 4: Travellers + Message -- */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label htmlFor={fid('travellers')} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1.5 block">
              Travellers <span className="text-gray-300 font-medium normal-case tracking-normal">(optional)</span>
            </label>
            <select
              id={fid('travellers')}
              name="travellers" value={form.travellers} onChange={handleChange}
              className={`${base} cursor-pointer`}
            >
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '10+'].map(n => (
                <option key={n} value={n}>
                  {n} {n === '1' ? 'person' : 'people'}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor={fid('message')} className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-1.5 block">
              Additional Details
            </label>
            <textarea
              id={fid('message')}
              name="message" value={form.message} onChange={handleChange}
              rows={3}
              placeholder="Budget, preferences, special requests..."
              className={`${base} resize-none`}
            />
          </div>
        </div>

        <button
          type="submit"
          className="w-full flex items-center justify-center gap-2 bg-[#0077CC] text-white py-4 rounded-xl font-black text-[13px] uppercase tracking-wider hover:bg-[#071126] transition-colors shadow-md"
        >
          <PaperPlaneTilt size={17} weight="fill" />
          {config.btnLabel}
        </button>

        <p className="text-center text-[11px] text-gray-400 font-medium">
          No spam. No account needed. We reply within 1 hour.
        </p>
      </form>
    </div>
  );
}

// -- Helper --------------------------------------------------------------------

function FieldError({ msg }: { msg: string }) {
  return (
    <p className="mt-1 text-[11px] text-red-500 font-bold flex items-center gap-1">
      <Warning size={11} weight="fill" /> {msg}
    </p>
  );
}
